import {
  ArrowForward as ArrowIcon,
  Category as CategoryIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  Inventory as ProductIcon,
  Search as SearchIcon,
  TrendingUp as TrendingIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Divider,
  Fade,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import CurrencyDisplay from '@shared/components/CurrencyDisplay';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../context/I18nContext';
import productService from '../../services/productService';
import { sanitizeImageUrl } from '../../utils/image';

const STORAGE_KEY = 'wasser_search_history';
const MAX_HISTORY = 5;
const DEBOUNCE_MS = 300;

// Category labels for grouping
const CATEGORY_LABELS = {
  vanities: 'Тумбы',
  cabinets: 'Пеналы',
  'mirror-cabinets': 'Зеркальные шкафы',
  'led-mirrors': 'LED зеркала',
  mirrors: 'Зеркала',
  bathtubs: 'Ванны',
  showers: 'Душевые кабины',
  toilets: 'Унитазы',
  sinks: 'Раковины',
  faucets: 'Смесители',
  'water-heaters': 'Водонагреватели',
};

/**
 * Highlight search query in text
 * @param {string} text - Text to highlight
 * @param {string} query - Search query
 * @returns {React.ReactNode}
 */
const HighlightText = ({ text, query }) => {
  if (!query || !text) return <>{text}</>;

  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedQuery})`, 'gi');
  const parts = text.split(regex);

  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <Box
            component="mark"
            key={i}
            sx={{
              bgcolor: 'warning.light',
              color: 'warning.dark',
              px: 0.25,
              borderRadius: 0.5,
            }}
          >
            {part}
          </Box>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </>
  );
};

const SearchAutocomplete = ({
  placeholder = 'Поиск товаров...',
  fullWidth = false,
  variant = 'outlined',
  size = 'medium',
}) => {
  const navigate = useNavigate();
  const { locale } = useI18n();
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [searchHistory, setSearchHistory] = useState([]);
  const [focused, setFocused] = useState(false);

  // Load search history
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        setSearchHistory(JSON.parse(stored));
      }
    } catch (e) {
      console.error('Error loading search history:', e);
    }
  }, []);

  // Save search history
  const saveToHistory = useCallback((searchQuery) => {
    if (!searchQuery.trim()) return;

    setSearchHistory((prev) => {
      const filtered = prev.filter(
        (q) => q.toLowerCase() !== searchQuery.toLowerCase()
      );
      const updated = [searchQuery, ...filtered].slice(0, MAX_HISTORY);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Clear history
  const clearHistory = useCallback(() => {
    setSearchHistory([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Search products
  const searchProducts = useCallback(async (searchQuery) => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const response = await productService.getProducts({
        search: searchQuery,
        limit: 6,
      });
      const products = response.data?.products || response.data || [];
      setResults(products.slice(0, 6));
    } catch (e) {
      console.error('Search error:', e);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.length >= 2) {
      debounceRef.current = setTimeout(() => {
        searchProducts(query);
      }, DEBOUNCE_MS);
    } else {
      setResults([]);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, searchProducts]);

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    setIsOpen(true);
  };

  const handleProductClick = (product) => {
    saveToHistory(query);
    setIsOpen(false);
    setQuery('');
    navigate(`/products/${product.slug || product.id}`);
  };

  const handleHistoryClick = (historyQuery) => {
    setQuery(historyQuery);
    searchProducts(historyQuery);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      saveToHistory(query);
      setIsOpen(false);
      navigate(`/products?search=${encodeURIComponent(query)}`);
    }
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    inputRef.current?.focus();
  };

  const getName = (product) => {
    const name = product?.name;
    if (!name) return '';
    return typeof name === 'object'
      ? name[locale] || name.ru || name.en || ''
      : name;
  };

  const getImage = (product) => {
    return (
      sanitizeImageUrl(
        product?.images?.thumbnailUrl ||
          product?.images?.urls?.[0] ||
          product?.imageUrl ||
          product?.image
      ) || '/images/placeholder-product.svg'
    );
  };

  // Group results by category
  const groupedResults = useMemo(() => {
    if (!results.length) return {};

    const groups = {};
    results.forEach((product) => {
      const category = product.category || product.category_slug || 'other';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(product);
    });

    return groups;
  }, [results]);

  const groupKeys = Object.keys(groupedResults);

  const showDropdown =
    isOpen && focused && (query.length >= 2 || searchHistory.length > 0);

  return (
    <ClickAwayListener onClickAway={() => setIsOpen(false)}>
      <Box
        sx={{
          position: 'relative',
          width: fullWidth ? '100%' : { xs: '100%', md: 400 },
        }}
      >
        <form onSubmit={handleSearchSubmit}>
          <TextField
            id="global-search"
            name="search"
            autoComplete="off"
            ref={inputRef}
            fullWidth
            size={size}
            variant={variant}
            placeholder={placeholder}
            value={query}
            onChange={handleInputChange}
            onFocus={() => {
              setFocused(true);
              setIsOpen(true);
            }}
            onBlur={() => setTimeout(() => setFocused(false), 200)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {loading && <CircularProgress size={20} />}
                  {query && !loading && (
                    <IconButton
                      size="small"
                      onClick={handleClear}
                      aria-label="Очистить поиск"
                      sx={{ minWidth: 44, minHeight: 44 }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
              sx: {
                borderRadius: 2,
                bgcolor: 'background.paper',
                '&:hover': { bgcolor: 'grey.50' },
              },
            }}
          />
        </form>

        {/* Dropdown */}
        <Fade in={showDropdown}>
          <Paper
            elevation={8}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              mt: 1,
              borderRadius: 2,
              maxHeight: 400,
              overflow: 'auto',
              zIndex: 1300,
            }}
          >
            {/* Search Results - Grouped by category */}
            {results.length > 0 && (
              <>
                {groupKeys.length > 1 ? (
                  // Grouped view when multiple categories
                  groupKeys.map((category, groupIdx) => (
                    <React.Fragment key={category}>
                      <Box
                        sx={{
                          px: 2,
                          py: 0.75,
                          bgcolor: 'grey.50',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <CategoryIcon
                          sx={{ fontSize: 16, color: 'text.secondary' }}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={600}
                        >
                          {CATEGORY_LABELS[category] || category}
                        </Typography>
                        <Chip
                          label={groupedResults[category].length}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.7rem',
                            ml: 'auto',
                          }}
                        />
                      </Box>
                      <List dense disablePadding>
                        {groupedResults[category].map((product) => (
                          <ListItem
                            key={product.id || product._id}
                            disablePadding
                          >
                            <ListItemButton
                              onClick={() => handleProductClick(product)}
                            >
                              <ListItemAvatar>
                                <Avatar
                                  src={getImage(product)}
                                  variant="rounded"
                                  sx={{ width: 48, height: 48 }}
                                />
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="body2"
                                    fontWeight={500}
                                    sx={{
                                      overflowWrap: 'anywhere',
                                      wordBreak: 'break-word',
                                    }}
                                  >
                                    <HighlightText
                                      text={getName(product)}
                                      query={query}
                                    />
                                  </Typography>
                                }
                                secondary={
                                  <CurrencyDisplay
                                    amount={
                                      product.price || product.pricing?.kgs
                                    }
                                    variant="body2"
                                    color="primary"
                                    fontWeight={600}
                                  />
                                }
                              />
                              <ArrowIcon fontSize="small" color="action" />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                      {groupIdx < groupKeys.length - 1 && <Divider />}
                    </React.Fragment>
                  ))
                ) : (
                  // Simple view when single category
                  <>
                    <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50' }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                      >
                        <ProductIcon
                          sx={{
                            fontSize: 14,
                            mr: 0.5,
                            verticalAlign: 'middle',
                          }}
                        />
                        Товары ({results.length})
                      </Typography>
                    </Box>
                    <List dense disablePadding>
                      {results.map((product) => (
                        <ListItem
                          key={product.id || product._id}
                          disablePadding
                        >
                          <ListItemButton
                            onClick={() => handleProductClick(product)}
                          >
                            <ListItemAvatar>
                              <Avatar
                                src={getImage(product)}
                                variant="rounded"
                                sx={{ width: 48, height: 48 }}
                              />
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Typography
                                  variant="body2"
                                  fontWeight={500}
                                  sx={{
                                    overflowWrap: 'anywhere',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  <HighlightText
                                    text={getName(product)}
                                    query={query}
                                  />
                                </Typography>
                              }
                              secondary={
                                <CurrencyDisplay
                                  amount={product.price || product.pricing?.kgs}
                                  variant="body2"
                                  color="primary"
                                  fontWeight={600}
                                />
                              }
                            />
                            <ArrowIcon fontSize="small" color="action" />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
                <Divider />
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={handleSearchSubmit}
                    sx={{ justifyContent: 'center', py: 1.5 }}
                  >
                    <Typography
                      variant="body2"
                      color="primary"
                      fontWeight={600}
                    >
                      Показать все результаты для &quot;{query}&quot;
                    </Typography>
                  </ListItemButton>
                </ListItem>
              </>
            )}

            {/* No Results */}
            {query.length >= 2 && results.length === 0 && !loading && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  Ничего не найдено по запросу &quot;{query}&quot;
                </Typography>
              </Box>
            )}

            {/* Search History */}
            {query.length < 2 && searchHistory.length > 0 && (
              <>
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    bgcolor: 'grey.50',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    <HistoryIcon
                      sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }}
                    />
                    Недавние поиски
                  </Typography>
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ cursor: 'pointer' }}
                    onClick={clearHistory}
                  >
                    Очистить
                  </Typography>
                </Box>
                <List dense disablePadding>
                  {searchHistory.map((historyQuery, idx) => (
                    <ListItem key={idx} disablePadding>
                      <ListItemButton
                        onClick={() => handleHistoryClick(historyQuery)}
                      >
                        <HistoryIcon
                          fontSize="small"
                          sx={{ mr: 1.5, color: 'text.secondary' }}
                        />
                        <ListItemText primary={historyQuery} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            {/* Popular searches (when empty) */}
            {query.length < 2 && searchHistory.length === 0 && (
              <>
                <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50' }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    <TrendingIcon
                      sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }}
                    />
                    Популярные запросы
                  </Typography>
                </Box>
                <Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {[
                    'Умывальник',
                    'Смеситель',
                    'Унитаз',
                    'Ванна',
                    'Душевая кабина',
                  ].map((term) => (
                    <Chip
                      key={term}
                      label={term}
                      size="small"
                      onClick={() => handleHistoryClick(term)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </>
            )}
          </Paper>
        </Fade>
      </Box>
    </ClickAwayListener>
  );
};

export default SearchAutocomplete;
